import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Paper, Grid, Stack, TextField, Button, Typography, Checkbox, FormControlLabel, Divider, Alert } from '@mui/material';
import { ArrowForwardIos, RestartAlt, AdminPanelSettings } from '@mui/icons-material';
import Notification from '../Notification';
import Progress from '../Progress';
import { useNavigate } from 'react-router-dom';

function AccessRoleForm({ initialData, onSuccess }) {
  const [values, setValues] = useState({ role_name: '', role_description: '' });
  const [selected, setSelected] = useState([]);
  const [groups, setGroups] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const navigate = useNavigate();

  const isEditMode = !!(initialData && initialData.role_id);

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/company-master/access-roles/permissions`);
        setGroups(response.data?.data || []);
      } catch (e) {
        setNotify({ isOpen: true, message: 'Failed to load permission list', type: 'error' });
      }
      setLoading(false);
    };
    loadCatalog();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      setValues({ role_name: initialData.role_name || '', role_description: initialData.role_description || '' });
      setSelected(initialData.permissions || []);
    } else {
      setValues({ role_name: '', role_description: '' });
      setSelected(['dashboard.view', 'help.view']);
    }
    setErrors({});
  }, [initialData, isEditMode]);

  const togglePermission = (key, checked) => {
    setSelected((prev) => (checked ? [...new Set([...prev, key])] : prev.filter((k) => k !== key)));
  };

  const toggleGroup = (group, checked) => {
    const keys = group.items.map((item) => item.key);
    setSelected((prev) => (checked ? [...new Set([...prev, ...keys])] : prev.filter((k) => !keys.includes(k))));
  };

  const groupState = (group) => {
    const count = group.items.filter((item) => selected.includes(item.key)).length;
    return { checked: count === group.items.length && count > 0, indeterminate: count > 0 && count < group.items.length };
  };

  const validate = () => {
    const temp = { role_name: values.role_name.trim() ? '' : 'Role name is required.' };
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const resetForm = () => {
    if (isEditMode) {
      setValues({ role_name: initialData.role_name || '', role_description: initialData.role_description || '' });
      setSelected(initialData.permissions || []);
    } else {
      setValues({ role_name: '', role_description: '' });
      setSelected(['dashboard.view', 'help.view']);
    }
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = { role_name: values.role_name.trim(), role_description: values.role_description.trim(), permissions: selected };
    const url = isEditMode
      ? `${process.env.REACT_APP_API_BASE_URL}/company-master/access-roles/${initialData.role_id}/update`
      : `${process.env.REACT_APP_API_BASE_URL}/company-master/access-roles`;

    setSubmitting(true);
    try {
      const response = await axios.post(url, payload);
      if (onSuccess) onSuccess(response.data.message);
    } catch (e) {
      if (e.response && e.response.status === 401) {
        setNotify({ isOpen: true, message: 'Session Expired!', type: 'error' });
        setTimeout(() => navigate('/'), 1000);
      } else {
        setNotify({ isOpen: true, message: e.response?.data?.message || e.response?.data?.error_message || 'Failed to save access role', type: 'error' });
      }
    }
    setSubmitting(false);
  };

  if (loading) return <Progress />;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', margin: '-15px', padding: '30px' }}>
      <Paper elevation={3} sx={{ width: '560px', marginTop: '-10px' }}>
        <Grid className="form-title" container spacing={2}>
          <Grid item>
            <Typography variant="h5" sx={{ textAlign: 'center', margin: 2 }}>
              {isEditMode ? 'Edit Access Role' : 'Add Access Role'}
            </Typography>
          </Grid>
          <Grid item>
            <AdminPanelSettings sx={{ width: 40, height: 40, marginLeft: '-20px' }} />
          </Grid>
        </Grid>
        <form autoComplete="off" onSubmit={handleSubmit}>
          <Stack spacing={2.5} sx={{ margin: '30px' }}>
            <TextField
              name="role_name"
              label={<span>Role Name<span style={{ color: 'red', marginLeft: '3px' }}>*</span></span>}
              size="small"
              inputProps={{ maxLength: 100 }}
              value={values.role_name}
              onChange={(e) => setValues((prev) => ({ ...prev, role_name: e.target.value }))}
              {...(errors.role_name && { error: true, helperText: errors.role_name })}
            />
            <TextField
              name="role_description"
              label="Description"
              size="small"
              multiline
              minRows={2}
              inputProps={{ maxLength: 255 }}
              value={values.role_description}
              onChange={(e) => setValues((prev) => ({ ...prev, role_description: e.target.value }))}
            />

            <div>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Menu Permissions</Typography>
              <Typography variant="body2" color="text.secondary">
                Tick the menus this role can open. Parent tick selects all menus under it.
              </Typography>
            </div>

            <Box sx={{ maxHeight: '320px', overflowY: 'auto', pr: 1 }}>
              {groups.map((group) => {
                const state = groupState(group);
                return (
                  <div key={group.key}>
                    <FormControlLabel
                      control={<Checkbox checked={state.checked} indeterminate={state.indeterminate} onChange={(e) => toggleGroup(group, e.target.checked)} />}
                      label={<b>{group.label}</b>}
                    />
                    <Stack sx={{ ml: 4 }}>
                      {group.items.map((item) => (
                        <FormControlLabel
                          key={item.key}
                          control={<Checkbox size="small" checked={selected.includes(item.key)} onChange={(e) => togglePermission(item.key, e.target.checked)} />}
                          label={item.label}
                        />
                      ))}
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                  </div>
                );
              })}
            </Box>

            {selected.length === 0 && (
              <Alert severity="warning">No menu is selected. Users with this role will only see Dashboard and Help.</Alert>
            )}

            <Grid container justifyContent="space-between">
              <Grid item>
                <Button className="form-btn" variant="contained" color="error" onClick={resetForm} endIcon={<RestartAlt />}>
                  Reset
                </Button>
              </Grid>
              <Grid item>
                <Button className="form-btn" variant="contained" type="submit" disabled={submitting} endIcon={<ArrowForwardIos sx={{ width: 18, height: 18 }} />}>
                  {submitting ? 'Saving...' : 'Submit'}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </form>
      </Paper>
      <Notification notify={notify} setNotify={setNotify} />
    </Box>
  );
}

export default AccessRoleForm;
